import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";

import { useProject } from "../app/projectContext";

import {
  createBreakerSelectivityRun,
  type BreakerSelectivityRunCreateRequest,
  type BreakerSelectivityRunResponse,
} from "../services/breakerSelectivity";

/**
 * Breaker duty and selectivity study mutation (EOS-05b).
 *
 * Mirrors useStarterCoordination:
 * - One in-flight request at a time: a new submit aborts the previous one.
 * - The in-flight request is aborted on unmount so no state update lands on
 *   an unmounted component.
 * - The run is attached to the revision chosen in the top bar, read at submit time; with
 *   no project, or one without an open revision, nothing is sent and the run is unassigned.
 * - Every calculation is persisted as a run (Master Prompt A16, A21); the typed
 *   result and the run summary are exposed exactly as parsed, including a
 *   NO_SOLUTION result - a breaker whose rating does not meet the fault level, or a
 *   required selectivity that is not met, is engineering evidence too.
 * - A linked fault run must belong to the revision the study is saved in; the backend
 *   says so (409) and the message reaches the page unchanged.
 *
 * The request is the whole run body, so the study's notes travel with it; the
 * form never puts a project revision in it.
 */
export function useBreakerSelectivity() {
  const controllerRef = useRef<AbortController | null>(null);
  const { activeRevisionId } = useProject();

  const mutation = useMutation<
    BreakerSelectivityRunResponse,
    Error,
    BreakerSelectivityRunCreateRequest
  >({
    mutationKey: ["electrical", "breaker-selectivity", "runs", "create"],
    mutationFn: (payload) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      return createBreakerSelectivityRun(
        payload,
        controller.signal,
        activeRevisionId() ?? undefined,
      );
    },
  });

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const calculate = useCallback(
    (payload: BreakerSelectivityRunCreateRequest) => mutation.mutateAsync(payload),
    [mutation],
  );

  const reset = useCallback(() => {
    controllerRef.current?.abort();
    mutation.reset();
  }, [mutation]);

  return {
    calculate,
    reset,
    result: mutation.data?.result ?? null,
    run: mutation.data?.run ?? null,
    error: mutation.error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
}

export type UseBreakerSelectivityResult = ReturnType<typeof useBreakerSelectivity>;
